import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Chip,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete
} from '@mui/material';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';

// Sortable Available Item Component
const SortableAvailableItem = ({ item }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `avail-${item.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{ 
        cursor: 'default', 
        userSelect: 'none',
        mb: 1,
        transform: isDragging ? 'rotate(5deg)' : 'none'
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main'
            },
            '&:active': {
              cursor: 'grabbing'
            }
          }}
        >
          <DragIndicatorIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {item.siNumber ? `SI: ${item.id}-${item.siNumber}` : `ID: ${item.id}`}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {item.buyingGrade || 'No Grade'} | {item.origin || 'N/A'} | Crop: {item.crop || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            N: {item.nicotine || 'N/A'}% | S: {item.sugar || 'N/A'}%
            {item.storageQuantity != null ? ` | Stk: ${item.storageQuantity}` : ''}
          </Typography>
          {item.customerGradeMark && (
            <Typography variant="caption" color="primary" noWrap>
              CGM: {item.customerGradeMark}
            </Typography>
          )}
          {(item.totalCostExclStorageUsd != null || item.salesPriceUsd != null) && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {item.totalCostExclStorageUsd != null && `Cost: $${Number(item.totalCostExclStorageUsd).toFixed(4)}`}
              {item.totalCostInclStorageUsd != null && ` (+Stg: $${Number(item.totalCostInclStorageUsd).toFixed(4)})`}
              {item.salesPriceUsd != null && ` | SP: $${Number(item.salesPriceUsd).toFixed(4)}${item.salesPriceOriginal != null && item.salesPriceCurrency && item.salesPriceCurrency !== 'USD' ? ` (${Number(item.salesPriceOriginal).toFixed(4)} ${item.salesPriceCurrency})` : ''}`}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Sortable Playground Item Component
const SortablePlaygroundItem = ({ item, index, onQuantityChange, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `playground-${item.id || index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <TableRow 
      ref={setNodeRef}
      style={style}
      sx={{
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        }
      }}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            {...attributes}
            {...listeners}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'grab',
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main'
              },
              '&:active': {
                cursor: 'grabbing'
              }
            }}
          >
            <DragIndicatorIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            {item.buyingGrade || item.description || (item.id ? `Item ${item.id}` : `Item ${index + 1}`)}
          </Box>
          <IconButton
            size="small"
            onClick={() => onRemove(index)}
            sx={{
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.light',
                color: 'white'
              }
            }}
            title="Remove item from playground"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
      <TableCell align="right">
        <TextField
          type="number"
          value={item.quantity || 0}
          onChange={(e) => onQuantityChange(index, e.target.value)}
          size="small"
        />
      </TableCell>
      <TableCell align="right">{item.percentage || '0.00'}%</TableCell>
      <TableCell align="right">{item.nicotine || '0.00'}</TableCell>
      <TableCell align="right">{item.nicotineContribution || '0.00'}%</TableCell>
      <TableCell align="right">{item.sugar || '0.00'}</TableCell>
      <TableCell align="right">{item.sugarContribution || '0.00'}%</TableCell>
      <TableCell align="right">{item.totalCostExclStorageUsd != null ? `$${Number(item.totalCostExclStorageUsd).toFixed(4)}` : '–'}</TableCell>
      <TableCell align="right">{item.totalCostInclStorageUsd != null ? `$${Number(item.totalCostInclStorageUsd).toFixed(4)}` : '–'}</TableCell>
      <TableCell align="right">
        {item.salesPriceUsd != null ? `$${Number(item.salesPriceUsd).toFixed(4)}` : '–'}
        {item.salesPriceOriginal != null && item.salesPriceCurrency && item.salesPriceCurrency !== 'USD'
          ? <Typography variant="caption" display="block" color="text.secondary">{`${Number(item.salesPriceOriginal).toFixed(4)} ${item.salesPriceCurrency}`}</Typography>
          : null}
      </TableCell>
    </TableRow>
  );
};

// Droppable Playground Area Component
const DroppablePlayground = ({ children }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: 'playground-droppable',
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: '200px',
        backgroundColor: isOver ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
        border: isOver ? '2px dashed #1976d2' : '2px dashed transparent',
        borderRadius: 1,
        transition: 'all 0.2s ease',
      }}
    >
      {children}
    </Box>
  );
};

// Droppable Remove Zone Component
const DroppableRemoveZone = () => {
  const { isOver, setNodeRef } = useDroppable({
    id: 'remove-zone',
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: isOver ? 'error.main' : 'error.light',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 3,
        transition: 'all 0.2s ease',
        transform: isOver ? 'scale(1.1)' : 'scale(1)',
        opacity: isOver ? 1 : 0.7,
        zIndex: 1000,
      }}
    >
      <DeleteIcon fontSize="large" />
    </Box>
  );
};

// Normalize item structure - handles both availableItems and currentItems formats
const normalizeItem = (item) => {
  if (!item) return null;
  
  return {
    id: String(item.id || item.itemId || item.item?.id || ''),
    buyingGrade: item.buyingGrade || item.item?.buyingGrade || item.description,
    crop: item.crop || item.item?.crop,
    origin: item.origin || item.item?.origin,
    nicotine: item.nicotine || item.item?.nicotine,
    sugar: item.sugar || item.item?.sugar,
    description: item.description || item.item?.description,
    quantity: item.quantity || 1,
    // SI-specific fields
    siId: item.siId,
    siNumber: item.siNumber,
    itemId: item.itemId,
    storageQuantity: item.storageQuantity,
    customerGradeMark: item.customerGradeMark,
    // Cost fields
    buyingPriceUsd: item.buyingPriceUsd,
    totalCostExclStorageUsd: item.totalCostExclStorageUsd,
    totalCostInclStorageUsd: item.totalCostInclStorageUsd,
    salesPriceUsd: item.salesPriceUsd,
    salesPriceOriginal: item.salesPriceOriginal,
    salesPriceCurrency: item.salesPriceCurrency,
    // Preserve any other properties
    ...item
  };
};

// Keep the calculateStats function as is
// normalizeItem is defined outside component - stable reference
const calculateStats = (items) => {
  if (!items || items.length === 0) return { 
    total: 0, 
    totalNicotine: 0,
    totalSugar: 0,
    itemsWithPercentages: [] 
  };

  const total = items.reduce((sum, item) => {
    const qty = Math.abs(Number(item.quantity)) || 0;
    return sum + qty;
  }, 0);

  const itemsWithPercentages = items.map(item => {
    const quantity = Math.abs(Number(item.quantity)) || 0;
    const percentage = total > 0 ? (quantity / total) * 100 : 0;
    const nicotineValue = Number(item.nicotine) || 0;
    const sugarValue = Number(item.sugar) || 0;
    const nicotineContribution = (percentage * nicotineValue) / 100;
    const sugarContribution = (percentage * sugarValue) / 100;

    return {
      ...item,
      percentage: percentage.toFixed(2),
      nicotineContribution: nicotineContribution.toFixed(2),
      sugarContribution: sugarContribution.toFixed(2)
    };
  });

  const totalNicotine = itemsWithPercentages.reduce(
    (sum, item) => sum + Number(item.nicotineContribution), 
    0
  ).toFixed(2);

  const totalSugar = itemsWithPercentages.reduce(
    (sum, item) => sum + Number(item.sugarContribution), 
    0
  ).toFixed(2);

  return { 
    total: Math.abs(total).toFixed(2), 
    totalNicotine, 
    totalSugar, 
    itemsWithPercentages 
  };
};

const VirtualizedList = React.memo(({ items, renderItem }) => {
  return (
    <Stack spacing={1}>
      {items.map((item, index) => renderItem(item, index))}
    </Stack>
  );
});

export default function BlendPlayground({ 
  currentItems = [], 
  availableItems = [], 
  onSave,
  onClose,
  versionSettings = null
}) {
  // Normalize the input data — wrapped in useMemo to avoid recreating on every render
  const normalizedCurrentItems = useMemo(
    () => currentItems.map(normalizeItem).filter(Boolean),
    [currentItems]
  );
  const normalizedAvailableItems = useMemo(
    () => availableItems.map(normalizeItem).filter(Boolean),
    [availableItems]
  );
  
  const [playgroundItems, setPlaygroundItems] = useState(normalizedCurrentItems);
  const [stats, setStats] = useState(calculateStats(normalizedCurrentItems));
  const [searchQuery, setSearchQuery] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [sortBy, setSortBy] = useState('buyingGrade');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({
    origin: [],
    crop: [],
    buyingGrade: '',
    nicotineMin: '',
    nicotineMax: '',
    sugarMin: '',
    sugarMax: ''
  });

  // Configure sensors for @dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setStats(calculateStats(playgroundItems));
  }, [playgroundItems]);

  const nicotineTolerancePct = Number(versionSettings?.nicotineTolerancePct) || 0;
  const sugarTolerancePct = Number(versionSettings?.sugarTolerancePct) || 0;
  const nicotineTargetMin = ((Number(stats.totalNicotine) || 0) * (1 - nicotineTolerancePct / 100)).toFixed(2);
  const sugarTargetMin = ((Number(stats.totalSugar) || 0) * (1 - sugarTolerancePct / 100)).toFixed(2);

  // Update playground items when currentItems change
  useEffect(() => {
    const newNormalizedItems = currentItems.map(normalizeItem).filter(Boolean);
    setPlaygroundItems(newNormalizedItems);
  }, [currentItems]);

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    if (!normalizedAvailableItems) return { origins: [], crops: [], buyingGrades: [] };
    
    const origins = [...new Set(normalizedAvailableItems.map(item => item.origin).filter(Boolean))].sort();
    const crops = [...new Set(normalizedAvailableItems.map(item => item.crop).filter(Boolean))].sort();
    const buyingGrades = [...new Set(normalizedAvailableItems.map(item => item.buyingGrade).filter(Boolean))].sort();
    
    return { origins, crops, buyingGrades };
  }, [normalizedAvailableItems]);

  const filteredAvailableItems = useMemo(() => {
    if (!normalizedAvailableItems) return [];
    
    return normalizedAvailableItems.filter(item => {
      if (!item) return false;
      
      // Text search
      const lowercaseQuery = searchQuery.toLowerCase();
      const searchString = [
        item.id,
        item.siId,
        item.siNumber,
        item.buyingGrade,
        item.customerGradeMark,
        item.crop,
        item.origin,
        item.nicotine,
        item.sugar,
        item.description
      ]
        .filter(Boolean)
        .map(val => String(val).toLowerCase())
        .join(' ');
      
      const matchesSearch = !searchQuery || searchString.includes(lowercaseQuery);
      
      // Filter by origin
      const matchesOrigin = !filters.origin || filters.origin.length === 0 || filters.origin.includes(item.origin);
      
      // Filter by crop
      const matchesCrop = !filters.crop || filters.crop.length === 0 || filters.crop.includes(item.crop);
      
      // Filter by buying grade
      const matchesBuyingGrade = !filters.buyingGrade || 
        (item.buyingGrade && item.buyingGrade.toLowerCase().includes(filters.buyingGrade.toLowerCase()));
      
      // Filter by nicotine range
      const nicotineValue = Number(item.nicotine) || 0;
      const matchesNicotineMin = !filters.nicotineMin || nicotineValue >= Number(filters.nicotineMin);
      const matchesNicotineMax = !filters.nicotineMax || nicotineValue <= Number(filters.nicotineMax);
      
      // Filter by sugar range
      const sugarValue = Number(item.sugar) || 0;
      const matchesSugarMin = !filters.sugarMin || sugarValue >= Number(filters.sugarMin);
      const matchesSugarMax = !filters.sugarMax || sugarValue <= Number(filters.sugarMax);
      
      return matchesSearch && matchesOrigin && matchesCrop && matchesBuyingGrade && 
             matchesNicotineMin && matchesNicotineMax && matchesSugarMin && matchesSugarMax;
    });
  }, [normalizedAvailableItems, searchQuery, filters]);

  const sortedAvailableItems = useMemo(() => {
    const items = [...filteredAvailableItems];

    const getNumeric = (value) => {
      const numeric = Number(value);
      return Number.isNaN(numeric) ? null : numeric;
    };

    items.sort((a, b) => {
      let compare = 0;

      if (sortBy === 'buyingGrade') {
        const aValue = (a.buyingGrade || '').toString().toLowerCase();
        const bValue = (b.buyingGrade || '').toString().toLowerCase();
        compare = aValue.localeCompare(bValue);
      } else {
        const aValue = getNumeric(a[sortBy]);
        const bValue = getNumeric(b[sortBy]);

        if (aValue == null && bValue == null) compare = 0;
        else if (aValue == null) compare = 1;
        else if (bValue == null) compare = -1;
        else compare = aValue - bValue;
      }

      return sortDirection === 'asc' ? compare : -compare;
    });

    return items;
  }, [filteredAvailableItems, sortBy, sortDirection]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setFilters({
      origin: [],
      crop: [],
      buyingGrade: '',
      nicotineMin: '',
      nicotineMax: '',
      sugarMin: '',
      sugarMax: ''
    });
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // Handle drag to remove zone
    if (overId === 'remove-zone' && activeId.startsWith('playground-')) {
      const activeIndex = playgroundItems.findIndex((item, index) => 
        `playground-${item.id || index}` === activeId
      );
      if (activeIndex !== -1) {
        handleRemoveItem(activeIndex);
      }
      return;
    }

    // Handle drag from available to playground
    if (activeId.startsWith('avail-') && (overId === 'playground-droppable' || overId.startsWith('playground-'))) {
      const itemId = activeId.replace('avail-', '');
      const item = normalizedAvailableItems.find(item => String(item.id) === itemId);
      
      if (item) {
        setPlaygroundItems(prev => [...prev, { ...item, quantity: 1 }]);
      }
      return;
    }

    // Handle reordering within playground
    if (activeId.startsWith('playground-') && overId.startsWith('playground-')) {
      const activeIndex = playgroundItems.findIndex((item, index) => 
        `playground-${item.id || index}` === activeId
      );
      const overIndex = playgroundItems.findIndex((item, index) => 
        `playground-${item.id || index}` === overId
      );

      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        setPlaygroundItems(arrayMove(playgroundItems, activeIndex, overIndex));
      }
    }
  };

  const handleQuantityChange = useCallback((index, value) => {
    setPlaygroundItems(prev => {
      const newItems = [...prev];
      newItems[index].quantity = value;
      return newItems;
    });
  }, []);

  const handleRemoveItem = useCallback((index) => {
    setPlaygroundItems(prev => {
      const newItems = [...prev];
      newItems.splice(index, 1);
      return newItems;
    });
  }, []);

  const handleSavePlayground = useCallback(() => {
    if (typeof onSave !== 'function') return;

    const itemsToSave = playgroundItems
      .map(item => ({
        ...item,
        quantity: Number(item.quantity) || 0,
      }))
      .filter(item => item.quantity > 0 && item.itemId && item.siId);

    onSave(itemsToSave);
  }, [onSave, playgroundItems]);

  return (
    <DndContext 
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
        <Paper sx={{ flex: 1, p: 2, maxWidth: 400 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Available Items</Typography>
          
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel id="available-items-sort-by-label">Sort By</InputLabel>
              <Select
                labelId="available-items-sort-by-label"
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="buyingGrade">Grade</MenuItem>
                <MenuItem value="totalCostExclStorageUsd">Cost (excl. storage)</MenuItem>
                <MenuItem value="totalCostInclStorageUsd">Cost (incl. storage)</MenuItem>
                <MenuItem value="salesPriceUsd">Selling Price (USD)</MenuItem>
                <MenuItem value="nicotine">Nicotine</MenuItem>
                <MenuItem value="sugar">Sugar</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 130 }}>
              <InputLabel id="available-items-sort-direction-label">Order</InputLabel>
              <Select
                labelId="available-items-sort-direction-label"
                value={sortDirection}
                label="Order"
                onChange={(e) => setSortDirection(e.target.value)}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Filters */}
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterListIcon />
                <Typography>Filters</Typography>
                {(filters.origin.length > 0 || filters.crop.length > 0 || filters.buyingGrade || filters.nicotineMin || 
                  filters.nicotineMax || filters.sugarMin || filters.sugarMax) && (
                  <Chip size="small" label="Active" color="primary" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {/* Dropdown Filters */}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={filterOptions.origins}
                      value={filters.origin}
                      onChange={(event, newValue) => handleFilterChange('origin', newValue)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Origin"
                          placeholder="Select origins..."
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={filterOptions.crops}
                      value={filters.crop}
                      onChange={(event, newValue) => handleFilterChange('crop', newValue)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Crop"
                          placeholder="Select crops..."
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Buying Grade"
                      placeholder="Type to filter by buying grade..."
                      value={filters.buyingGrade}
                      onChange={(e) => handleFilterChange('buyingGrade', e.target.value)}
                      InputProps={{
                        endAdornment: filters.buyingGrade && (
                          <InputAdornment position="end">
                            <IconButton 
                              size="small" 
                              onClick={() => handleFilterChange('buyingGrade', '')}
                            >
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Range Filters */}
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Nicotine Range (%)</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Min"
                      value={filters.nicotineMin}
                      onChange={(e) => handleFilterChange('nicotineMin', e.target.value)}
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Max"
                      value={filters.nicotineMax}
                      onChange={(e) => handleFilterChange('nicotineMax', e.target.value)}
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" sx={{ mt: 1 }}>Sugar Range (%)</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Min"
                      value={filters.sugarMin}
                      onChange={(e) => handleFilterChange('sugarMin', e.target.value)}
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Max"
                      value={filters.sugarMax}
                      onChange={(e) => handleFilterChange('sugarMax', e.target.value)}
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  </Grid>
                </Grid>

                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={clearAllFilters}
                  startIcon={<ClearIcon />}
                >
                  Clear All Filters
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>
          
          <Box sx={{ maxHeight: '50vh', overflowY: 'auto', padding: 1 }}>
            <SortableContext 
              items={sortedAvailableItems.map(item => `avail-${item.id}`)}
              strategy={verticalListSortingStrategy}
            >
              <VirtualizedList
                items={sortedAvailableItems}
                renderItem={(item, index) => (
                  <SortableAvailableItem key={`avail-${item.id}`} item={item} />
                )}
              />
            </SortableContext>
          </Box>
        </Paper>

        <Paper sx={{ flex: 2, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">Playground</Typography>
              <Typography variant="caption" color="text.secondary">
                Cut Width: {versionSettings?.cutWidth || '-'} | Moisture: {versionSettings?.moisture || '-'} | Temperature: {versionSettings?.temperature || '-'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSavePlayground}
                disabled={playgroundItems.length === 0}
              >
                Save To Blend
              </Button>
              {playgroundItems.length > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => setPlaygroundItems([])}
                >
                  Clear All
                </Button>
              )}
            </Stack>
          </Box>

          <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1, backgroundColor: 'rgba(25, 118, 210, 0.06)' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Tolerance Adjusted Minimums
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Nicotine: {stats.totalNicotine}% - {nicotineTolerancePct.toFixed(2)}% = {nicotineTargetMin}% | Sugar: {stats.totalSugar}% - {sugarTolerancePct.toFixed(2)}% = {sugarTargetMin}%
            </Typography>
          </Box>
          
          <DroppablePlayground>
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Percentage</TableCell>
                    <TableCell align="right">Nicotine Value</TableCell>
                    <TableCell align="right">Nicotine %</TableCell>
                    <TableCell align="right">Sugar Value</TableCell>
                    <TableCell align="right">Sugar %</TableCell>
                    <TableCell align="right">Unit Cost (excl. Stg)</TableCell>
                    <TableCell align="right">Unit Cost (incl. Stg)</TableCell>
                    <TableCell align="right">Selling Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <SortableContext 
                    items={stats.itemsWithPercentages.map((item, index) => `playground-${item.id || index}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    {stats.itemsWithPercentages.map((item, index) => (
                      <SortablePlaygroundItem
                        key={`playground-${item.id || index}`}
                        item={item}
                        index={index}
                        onQuantityChange={handleQuantityChange}
                        onRemove={handleRemoveItem}
                      />
                    ))}
                  </SortableContext>
                  <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                    <TableCell align="center" colSpan={2} sx={{ fontWeight: 'bold' }}>Total:</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>100.00%</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>-</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{stats.totalNicotine}%</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>-</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{stats.totalSugar}%</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {stats.itemsWithPercentages.every(i => i.totalCostExclStorageUsd != null)
                        ? `$${stats.itemsWithPercentages.reduce((acc, i) => acc + Number(i.totalCostExclStorageUsd) * (Number(i.percentage) / 100), 0).toFixed(4)}`
                        : '–'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {stats.itemsWithPercentages.every(i => i.totalCostInclStorageUsd != null)
                        ? `$${stats.itemsWithPercentages.reduce((acc, i) => acc + Number(i.totalCostInclStorageUsd) * (Number(i.percentage) / 100), 0).toFixed(4)}`
                        : '–'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {stats.itemsWithPercentages.every(i => i.salesPriceUsd != null)
                        ? `$${stats.itemsWithPercentages.reduce((acc, i) => acc + Number(i.salesPriceUsd) * (Number(i.percentage) / 100), 0).toFixed(4)}`
                        : '–'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </DroppablePlayground>
        </Paper>
      </Box>
      {/* Show remove zone only when dragging playground items */}
      {activeId && activeId.startsWith('playground-') && <DroppableRemoveZone />}
      <DragOverlay>
        {activeId ? (
          activeId.startsWith('avail-') ? (
            <Card sx={{ opacity: 0.8, transform: 'rotate(5deg)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
                <DragIndicatorIcon />
                <Typography>Dragging item...</Typography>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ 
              backgroundColor: 'white', 
              border: '1px solid #ccc', 
              borderRadius: 1, 
              p: 1,
              opacity: 0.8 
            }}>
              <Typography>Dragging playground item... (Drag to trash to remove)</Typography>
            </Box>
          )
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
